import { useEffect } from "react";

/**
 * Global Escape-to-close handler for every dialog in the app.
 *
 * Mounts once at the App root. On each Escape keystroke it locates
 * the topmost `[role="dialog"]` element (last one in DOM order = last
 * mounted = visually on top, since no modal here uses React portals)
 * and simulates a click on the outermost `.fixed.inset-0` backdrop
 * wrapping it. Every modal in the codebase already has a backdrop
 * onClick that closes — either `onClose` directly or a guarded
 * `e.target === e.currentTarget` check. A synthetic click on the
 * backdrop element itself satisfies both patterns because the click
 * target equals currentTarget.
 *
 * This mounts-once approach means we didn't have to touch every
 * modal component to add its own Escape effect — a single listener
 * fans out to all of them via the DOM.
 *
 * If nothing is clickable via the heuristic (e.g. a future modal
 * without a `.fixed.inset-0` backdrop), we fall back to clicking the
 * dialog element itself so at least the operator gets a best-effort
 * close.
 */
export default function EscapeToClose() {
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Escape") return;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs.length === 0) return;
      // Topmost = last in DOM order (no portals in this codebase, so
      // mount order == DOM order == z-index stacking order).
      const top = dialogs[dialogs.length - 1];
      // Walk up to find the backdrop layer — the element that owns
      // the onClick-to-close. Most modals put `role="dialog"` on the
      // backdrop itself, but TwoFAResetModal places it on the inner
      // box and the backdrop is one level up. Both cases resolve
      // correctly via this walk.
      let backdrop = top;
      while (backdrop && backdrop !== document.body) {
        const cls =
          typeof backdrop.className === "string" ? backdrop.className : "";
        if (cls.includes("fixed") && cls.includes("inset-0")) break;
        backdrop = backdrop.parentElement;
      }
      (backdrop && backdrop !== document.body ? backdrop : top).click();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  return null;
}
